import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  Injector,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { Router } from '@angular/router';
import { isEqual, some, uniq, without } from 'lodash-es';
import { DateTime } from 'luxon';
import { MonacoEditorModule } from 'ngx-monaco-editor';
import { catchError, firstValueFrom, forkJoin, interval, map, of, Subscription, switchMap, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { LoadingComponent } from '../../../component/loading/loading.component';
import { NavComponent } from '../../../component/nav/nav.component';
import { SelectPluginComponent } from '../../../component/select-plugin/select-plugin.component';
import { FakeLinkDirective } from '../../../directive/fake-link.directive';
import { ResizeHandleDirective } from '../../../directive/resize-handle.directive';
import { EditorComponent } from '../../../form/editor/editor.component';
import { LinksFormComponent } from '../../../form/links/links.component';
import { PluginsFormComponent, writePlugins } from '../../../form/plugins/plugins.component';
import { refForm, RefFormComponent, setRefForm } from '../../../form/ref/ref.component';
import { addTags, fillTags, TagsFormComponent } from '../../../form/tags/tags.component';
import { HasChanges } from '../../../guard/pending-changes.guard';
import { Ext } from '../../../model/ext';
import { Ref } from '../../../model/ref';
import { wikiTitleFormat, wikiUriFormat } from '../../../mods/org/wiki';
import { AdminService } from '../../../service/admin.service';
import { ExtService } from '../../../service/api/ext.service';
import { RefService } from '../../../service/api/ref.service';
import { TaggingService } from '../../../service/api/tagging.service';
import { BookmarkService } from '../../../service/bookmark.service';
import { ConfigService } from '../../../service/config.service';
import { EditorService } from '../../../service/editor.service';
import { ModService } from '../../../service/mod.service';
import { Store } from '../../../store/store';
import { readFileAsString } from '../../../util/async';
import { controlState, controlValue, scrollToFirstInvalid } from '../../../util/form';
import { printError } from '../../../util/http';
import { getVisibilityTags, hasPrefix, hasTag } from '../../../util/tag';

@Component({
  selector: 'app-submit-text',
  templateUrl: './text.component.html',
  styleUrls: ['./text.component.scss'],
  host: { 'class': 'full-page-form' },
  imports: [
    FakeLinkDirective,
    forwardRef(() => EditorComponent),
    ReactiveFormsModule,
    NavComponent,
    LoadingComponent,
    SelectPluginComponent,
    PluginsFormComponent,
    MonacoEditorModule,
    ResizeHandleDirective,
    TagsFormComponent,
    forwardRef(() => RefFormComponent),
  ],
})
export class SubmitTextPage implements HasChanges {
  config = inject(ConfigService);
  private mod = inject(ModService);
  admin = inject(AdminService);
  private router = inject(Router);
  store = inject(Store);
  bookmarks = inject(BookmarkService);
  private editor = inject(EditorService);
  private refs = inject(RefService);
  private exts = inject(ExtService);
  private ts = inject(TaggingService);
  private fb = inject(UntypedFormBuilder);



  private readonly injector = inject(Injector);
  private generatedUrl = 'comment:' + uuid();

  readonly submitted = signal<boolean>(false);
  textForm: UntypedFormGroup;
  protected readonly textFormValid = controlState(() => this.textForm, c => c.valid);
  protected readonly textFormPristine = controlState(() => this.textForm, c => c.pristine);
  readonly advanced = signal<boolean>(false);
  readonly serverError = signal<string[]>([]);

  readonly editorComponent = viewChild<EditorComponent>('ed');

  readonly tagsFormComponent = viewChild.required<TagsFormComponent>('tagsFormComponent');
  /**
   * Plugin data the plugin forms are built from.
   */
  readonly pluginValues = signal<Record<string, any> | undefined>(undefined);

  readonly submitting = signal(false);
  private submittingSubscription?: Subscription;
  readonly saving = signal(false);
  private pendingSave?: Promise<string>;
  private lastSaved?: string;
  private autosaveFailed = false;
  addAnother = false;
  readonly defaults = signal<{ url: string, ref: Partial<Ref> } | undefined>(undefined);
  readonly loadingDefaults = signal<Ext[]>([]);
  readonly completedUploads = signal<Ref[]>([]);
  private oldSubmit: string[] = [];
  /**
   * The Ref the advanced form is filled from.
   */
  readonly savedRef = signal<Ref | undefined>(undefined);
  private cursor?: string;
  private readonly tagsValue = controlValue<string[]>(() => this.tags);

  constructor() {
    const mod = this.mod;
    const admin = this.admin;
    const store = this.store;
    const fb = this.fb;

    mod.setTitle($localize`Submit: Text Post`);
    this.textForm = refForm(fb);
    this.ensureUrl();
    store.submit.wikiPrefix.set(admin.getWikiPrefix());
    this.initialize();
    if (admin.editing()) {
      interval(5_000).pipe(
        takeUntilDestroyed(),
      ).subscribe(() => {
        if (this.textForm.dirty && this.comment.enabled && !this.pendingSave && !this.submitting()) this.autosave();
      });
    }
  }

  addCompletedUpload(ref: Ref) {
    this.completedUploads.update(uploads => [...uploads, ref]);
    this.textForm.markAsDirty();
  }

  /**
   * Saves once per tick and shows a failure only once until a save succeeds.
   */
  private autosave() {
    this.saveDraft().then(
      () => this.autosaveFailed = false,
      (err: HttpErrorResponse) => {
        if (this.autosaveFailed) return;
        this.autosaveFailed = true;
        this.serverError.set(printError(err));
      },
    );
  }

  async saveChanges() {
    if (!this.admin.editing()) return !this.textForm?.dirty;
    try {
      await this.flushDraft();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Shares the save in flight so only one write uses each cursor.
   */
  private saveDraft() {
    if (!this.pendingSave) {
      const savedValue = JSON.stringify(this.textForm.value);
      this.saving.set(true);
      this.pendingSave = firstValueFrom(this.refs.saveEdit(this.writeRef(), this.cursor))
        .then(cursor => {
          this.cursor = cursor;
          this.lastSaved = savedValue;
          if (JSON.stringify(this.textForm.value) === savedValue) this.textForm.markAsPristine();
          return cursor;
        })
        .finally(() => {
          this.saving.set(false);
          this.pendingSave = undefined;
        });
    }
    return this.pendingSave;
  }

  /**
   * Waits for any save in flight, then saves edits made since its snapshot.
   */
  private async flushDraft() {
    for (let round = 0; round < 5 && (this.pendingSave || this.textForm.dirty); round++) {
      await this.saveDraft();
      if (!this.pendingSave && JSON.stringify(this.textForm.value) === this.lastSaved) break;
    }
  }

  private initialize() {
    if (this.admin.editing() && this.store.submit.url()) {
      this.refs.getEditing(this.store.submit.url()).subscribe(draft => {
        if (!draft) return;
        this.cursor = draft.modifiedString;
        const edit = draft.plugins?.['plugin/editing'] || {};
        if (edit.comment) this.comment.setValue(edit.comment);
        if (edit.title) this.title.setValue(edit.title);
      });
    }
    const allTags = [...this.store.submit.tags(), ...(this.store.account.localTag() ? [this.store.account.localTag()] : [])];
    this.exts.getCachedExts(allTags).pipe(
      map(xs => xs.filter(x => x.config?.defaults) as Ext[]),
      switchMap(xs => {
        this.loadingDefaults.set(xs);
        return this.refs.getDefaults(...xs.map(x => x.tag))
      }),
    ).subscribe(d => {
      this.loadingDefaults.set([]);
      this.defaults.set(d);
      if (d) {
        this.oldSubmit = uniq([...allTags, ...Object.keys(d.ref.plugins || {})]);
        this.addTag(...this.oldSubmit);
        this.pluginValues.set(d.ref.plugins);
        this.textForm.patchValue({
          ...d.ref,
          tags: this.oldSubmit,
        });
      }
      if (this.store.account.localTag()) this.addTag(this.store.account.localTag());
      let initialRun = true;
      effect(() => {
        this.store.submit.url();
        this.store.submit.wiki();
        this.store.submit.title();
        this.store.submit.tags();
        this.store.account.localTag();
        this.store.submit.pluginUpload();
        this.store.submit.plugin();
        this.store.submit.sources();
        untracked(() => {
          const url = this.ensureUrl();
          if (!this.admin.isWikiExternal() && this.store.submit.wiki()) {
            this.mod.setTitle($localize`Submit: Wiki`);
            this.title.setValue(wikiTitleFormat(url, this.admin.getWikiPrefix()));
            this.title.disable();
          } else if (this.store.submit.title()) {
            this.title.setValue(this.store.submit.title());
          }
          const tags = [...this.store.submit.tags(), ...(this.store.account.localTag() ? [this.store.account.localTag()] : [])];
          const added = without(tags, ...this.oldSubmit);
          const removed = without(this.oldSubmit, ...tags);
          if (added.length || removed.length) {
            this.oldSubmit = uniq([...without(this.oldSubmit, ...removed), ...added]);
            // Only apply changes so tags edited in the form are kept
            if (removed.length) {
              fillTags(this.fb, this.tags, without(this.tags.value, ...removed));
            }
            if (added.length) this.addTag(...added);
          }
          if (this.store.submit.pluginUpload()) {
            this.addTag(this.store.submit.plugin());
            this.pluginValues.set({
              ...this.textForm.value.plugins || {},
              [this.store.submit.plugin()]: { url: this.store.submit.pluginUpload() },
            });
            if (this.store.submit.plugin() === 'plugin/image' || this.store.submit.plugin() === 'plugin/video') {
              this.addTag('plugin/thumbnail');
            }
          }
          for (const s of this.store.submit.sources()) {
            this.addSource(s)
          }
          // Later runs come from the user picking plugins or tags
          if (!initialRun) this.textForm.markAsDirty();
          initialRun = false;
        });
      }, { injector: this.injector });
      // Seed the location once so later tag changes keep any edits
      const location = this.store.submit.location();
      if (location) {
        this.addTag('plugin/geo/point');
        this.pluginValues.set({
          ...this.textForm.value.plugins || {},
          'plugin/geo/point': { type: 'Feature', geometry: { type: 'Point', coordinates: location } },
        });
      }
      if (this.store.submit.embedFiles().length) {
        const files = [...this.store.submit.embedFiles()];
        // The editor uploads the files once it has rendered
        afterNextRender(() => {
          const editorComponent = this.editorComponent();
          if (this.customEditor()) {
            this.store.submit.setEmbedFiles();
            forkJoin(files.map(f => readFileAsString(f))).subscribe(texts => {
              this.comment.setValue(texts.join('\n'));
              this.comment.markAsDirty();
            });
          } else if (editorComponent instanceof EditorComponent) {
            this.store.submit.setEmbedFiles();
            editorComponent.upload(files as any);
          }
        }, { injector: this.injector });
      }
    });
  }

  readonly randomURL = computed(() => {
    return !this.store.submit.url() && (this.admin.isWikiExternal() || !this.store.submit.wiki()) ;
  });

  saveForLater(leave = false) {
    this.flushDraft().then(
      () => leave && this.router.navigate(['/inbox/ref', 'plugin/editing']),
      (err: HttpErrorResponse) => this.serverError.set(printError(err)),
    );
  }

  showAdvanced() {
    const tags = uniq(this.textForm.value.tags);
    const published = this.textForm.value.published ? DateTime.fromISO(this.textForm.value.published) : DateTime.now();
    const ref: Ref = {
      ...this.textForm.value,
      url: this.url.value, // Need to pull separately since control is locked
      title: this.title.value, // Need to pull separately if disabled by wiki mode
      origin: this.store.account.origin(),
      published,
      tags,
      plugins: writePlugins(this.textForm.value.tags, this.textForm.value.plugins),
    };
    setRefForm(this.fb, this.admin, this.textForm, ref);
    this.savedRef.set(ref);
    this.advanced.set(true);
  }

  get url() {
    return this.textForm.get('url') as UntypedFormControl;
  }

  get title() {
    return this.textForm.get('title') as UntypedFormControl;
  }

  get comment() {
    return this.textForm.get('comment') as UntypedFormControl;
  }

  get sources() {
    return this.textForm.get('sources') as UntypedFormArray;
  }

  get tags() {
    return this.textForm.get('tags') as UntypedFormArray;
  }

  readonly codeLang = computed(() => {
    for (const t of this.tagsValue() || []) {
      if (hasPrefix(t, 'plugin/code')) {
        return t.split('/')[2];
      }
    }
    return '';
  });

  readonly codeOptions = computed(() => ({
    language: this.codeLang(),
    theme: this.store.darkTheme() ? 'vs-dark' : 'vs',
    automaticLayout: true,
  }), { equal: isEqual });

  readonly customEditor = computed(() => {
    const tags = this.tagsValue();
    if (!tags) return false;
    return some(this.admin.editor(), t => hasTag(t.tag, tags));
  });

  togglePlugin(tag: string) {
    if (!tag) return;
    if (hasTag(tag, this.tags.value)) {
      this.tagsFormComponent().removeTagAndChildren(tag);
    } else {
      this.addTag(tag);
    }
    this.textForm.markAsDirty();
  }

  setTags(value: string[]) {
    fillTags(this.fb, this.tags, value);
  }

  validate(input: HTMLInputElement) {
    if (this.title.touched) {
      if (this.title.errors?.['required']) {
        input.setCustomValidity($localize`Title must not be blank.`);
        input.reportValidity();
      }
    }
  }

  addTag(...values: string[]) {
    addTags(this.fb, this.tags, ...values);
    this.submitted.set(false);
  }

  private top() {
    return this.sources.value[1] || this.sources.value[0] || this.ensureUrl();
  }

  addSource(value = '') {
    while (this.sources.value.length < 2) {
      this.sources.push(this.fb.control(this.top(), LinksFormComponent.validators));
    }
    this.sources.push(this.fb.control(value, LinksFormComponent.validators));
    this.submitted.set(false);
  }

  private ensureUrl() {
    const routeUrl = this.store.submit.url();
    const currentUrl = this.url.value;
    const wiki = this.store.submit.wiki();
    const wikiPrefix = this.admin.getWikiPrefix();
    const useRouteUrl = !!routeUrl && (!wiki || routeUrl !== wikiPrefix);
    let url = useRouteUrl ? routeUrl : currentUrl || this.generatedUrl;
    if (!this.admin.isWikiExternal() && wiki) {
      url = wikiUriFormat(url, wikiPrefix);
    }
    if (this.url.value !== url) this.url.setValue(url);
    this.url.disable();
    return url;
  }

  syncEditor() {
    this.editor.syncEditor(this.fb, this.textForm);
  }

  writeRef(publish = false) {
    const url = this.ensureUrl();
    return <Ref> {
      ...this.textForm.value,
      url, // Need to pull separately since control is locked
      title: this.title.value, // Need to pull separately if disabled by wiki mode
      origin: this.store.account.origin(),
      published: this.textForm.value.published ? DateTime.fromISO(this.textForm.value.published) : publish ? DateTime.now() : undefined,
      tags: uniq(this.textForm.value.tags),
      plugins: writePlugins(this.textForm.value.tags, this.textForm.value.plugins),
    };
  }

  submit() {
    if (this.pendingSave) {
      this.pendingSave.then(() => this.submit(), () => this.submit());
      return;
    }
    this.serverError.set([]);
    this.submitted.set(true);
    this.textForm.markAllAsTouched();
    this.syncEditor();
    if (!this.textForm.valid) {
      scrollToFirstInvalid();
      return;
    }
    const ref = this.writeRef(true);
    const tags = ref.tags;
    const published = ref.published;
    this.submitting.set(true);
    this.submittingSubscription = (this.cursor ? this.refs.update({ ...ref, modifiedString: this.cursor }) : this.refs.create(ref)).pipe(
      tap(() => {
        if (this.admin.getPlugin('plugin/user/vote/up')) {
          this.ts.createResponse('plugin/user/vote/up', this.url.value).subscribe();
        }
      }),
      switchMap(res => {
        const finalVisibilityTags = getVisibilityTags(tags);
        if (!finalVisibilityTags.length) return of(res);
        const taggingOps = this.completedUploads()
          .map(upload => this.ts.patch(finalVisibilityTags, upload.url, upload.origin));
        if (!taggingOps.length) return of(res);
        return forkJoin(taggingOps).pipe(map(() => res));
      }),
      catchError((res: HttpErrorResponse) => {
        this.submitting.set(false);
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    ).subscribe(() => {
      this.submitting.set(false);
      this.textForm.markAsPristine();
      this.completedUploads.set([]);

      if (this.addAnother) {
        this.cursor = undefined;
        this.url.enable();
        this.url.setValue('comment:' + uuid());
        this.url.disable();
      } else if (hasTag('plugin/thread', ref)) {
        this.router.navigate(['/ref', this.url.value, 'thread'], { queryParams: { published }, replaceUrl: true });
      } else {
        this.router.navigate(['/ref', this.url.value], { queryParams: { published }, replaceUrl: true});
      }
    });
    this.submittingSubscription?.add(() => this.submitting.set(false));
  }
}
